import { Box } from '@mui/material';
import { humanizeDate, humanizeDateVariants } from '../../../../../helpers/humanizeDate';
import { IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { ArrowLeft } from '@sweep-io/sweep-design/dist/icons';
import { useSelector } from 'react-redux';
import { selectAccountUsers } from '../../../../../../reducers/accountUsersReducer';
import { ParsedRecordType } from '../../../../../parser/ParserTypes';
import { NotDeployedTag } from '../main-view/NotDeployedTag';
import { TruncatedTextTooltip } from '../../../../../common/TruncatedTextTooltip';
import { AddFunnelButton } from '../main-view/AddFunnelButton';

interface PreviewHeaderProps {
  isLoadingButton: boolean;
  funnelStructure?: ShortFunnel;
  selectedRecordType?: ParsedRecordType;
  backToMainView: () => void;
  onAddItemToMapClick?: (selectedRecordType?: ParsedRecordType, funnel?: ShortFunnel) => void;
  isError?: boolean;
}

export const PreviewHeader = ({
  funnelStructure,
  selectedRecordType,
  onAddItemToMapClick,
  backToMainView,
  isLoadingButton,
  isError,
}: PreviewHeaderProps) => {
  const users = useSelector(selectAccountUsers);
  const updatedByName = users?.find((user) => user.id === funnelStructure?.updatedById)?.name;
  if (!funnelStructure) return;

  return (
    <Box
      sx={{
        display: 'flex',
        alignContent: 'flex-start',
        width: '100%',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignContent: 'center',
          flexWrap: 'nowrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton variant="flat" size="tiny" onClick={backToMainView}>
            <ArrowLeft />
          </IconButton>
          <Typography variant="h4">{funnelStructure.name}</Typography>
        </Box>

        {!isError && (
          <Box ml={1}>
            <AddFunnelButton
              startIconName="Plus"
              onAddClick={() =>
                onAddItemToMapClick && onAddItemToMapClick(selectedRecordType, funnelStructure)
              }
              size="small"
              isLoading={isLoadingButton}
            />
          </Box>
        )}
      </Box>

      {!isError && funnelStructure.id !== 'MAPPED_RECORD_TYPE' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 0 }}>
          {!funnelStructure.isDeployed && <NotDeployedTag />}

          {(updatedByName || funnelStructure.updatedAt) && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TruncatedTextTooltip variant="caption" color={colors.grey[800]}>
                Last edited
                {!!updatedByName ? ' by ' + updatedByName : ' '}
                <Box component="span" sx={{ pl: 0.5, pr: 0.5 }}>
                  •
                </Box>
                {humanizeDate({
                  dateOrTimestamp: funnelStructure.updatedAt ?? '',
                  variant: humanizeDateVariants.SHORT_WITH_TIME,
                })}
              </TruncatedTextTooltip>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
